// R5.3 — THE CORRECTION LOOP'S CLIENT HALF: `If-Match` STATE MANAGEMENT + ONE TYPED PATCH (spec §5, §1.5).
//
// THIS MODULE IS DELIBERATELY NOT A COMPONENT. Everything the correction loop has to get right is here, as pure
// functions plus one network call, so it can be tested without rendering anything: which precondition may be
// presented, when no precondition may be presented at all, what each typed server answer means, and what the
// panel is allowed to do next. A component that owned this logic inline would only be testable through the DOM,
// and the properties that matter most — "a superseded CV's precondition is never presented against a new CV",
// "a conflict clears the token rather than retrying with it" — are exactly the ones a DOM test states weakly.
//
// THE FOUR RULES OF THE PRECONDITION, and why each is a rule rather than a habit:
//
//   1. IT IS OPAQUE. The client stores the exact string the server last issued and presents it back. It never
//      assembles one from parts and never increments one itself, because a self-assembled precondition asserts a
//      state the client did not observe — which is precisely the assertion §1.5 built the precondition to refuse.
//      (The one exception is the FIRST-EXTRACTION SENTINEL, which names the CV and no revision at all; it is
//      built through the shared `rubricPrecondition.ts` grammar, the same function the server's guard key uses.)
//
//   2. IT IS SCOPED TO ONE CV, AND RESET ON EVERY SOURCE CHANGE. Preconditions carry ledger ids, which are not
//      interchangeable across CVs; a token held across a `cvId` change would be presented against a ledger it
//      does not name. `reset` clears it SYNCHRONOUSLY, before the next request is issued — the same rule §1.5
//      (rev 14, finding 8) imposes on the evidence/entry-label/ledger maps, for the same reason: the worst case
//      must be an affordance that does not appear, never a write against somebody else's state.
//
//   3. NO TOKEN ⇒ NO WRITE. Every state in which the client does not hold a server-issued precondition disables
//      editing and says so with the registered `CS-EDIT-DISABLED`. Sending a PATCH without one would earn a
//      `428 precondition_required`; sending a guessed one would earn a `409` that is indistinguishable, to the
//      applicant, from "somebody else changed this review".
//
//   4. ONE CORRECTION AT A TIME. Two in-flight PATCHes necessarily present the SAME precondition, so the second
//      is a guaranteed guard collision — the client would be manufacturing the conflict it then reports. The
//      submit gate refuses while a correction is in flight.
//
// WHAT A CONFLICT DOES, EXACTLY (§5, `CS-CORRECT-CONFLICT`): it CLEARS the token and raises the notice. It does
// not retry, does not rebase, and does not silently re-present a fresher token the response happened to carry —
// "the client re-fetches and re-presents" is a user-visible sequence, and a client that auto-retried would apply
// the applicant's edit on top of a change they never saw.
import { api } from '../lib/api';
import {
  EDITABLE_FIELDS, UI_EDITABLE_FIELDS, inertString, maxLengthFor,
} from '../../../workers/src/lib/factEdit';
// The typed-value vocabulary: which KIND a field's value is, and which registered label each enum member renders
// under. Shared with the worker so the surface and the route cannot disagree about either.
import { presentationFor } from '../../../workers/src/lib/factLabels';
import { firstExtractionPrecondition } from '../../../workers/src/lib/rubricPrecondition';
import { isValidPublicShape } from '../../../workers/src/lib/rubricReadShape';
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';

export { UI_EDITABLE_FIELDS, maxLengthFor, firstExtractionPrecondition };

/* ============================================================================================================== *
 * THE STATE
 * ============================================================================================================== */

/** The composite `If-Match`, exactly as the server issued it. Opaque here, by rule 1. */
export type Precondition = string;

export interface CorrectionState {
  /** The CV this token belongs to. A token is only ever valid for the CV whose response carried it. */
  cvId: string;
  /** `null` ⇒ no write is permitted (rule 3). */
  precondition: Precondition | null;
  /** In flight (rule 4). */
  busy: boolean;
  /**
   * The server told us the review moved under us — `409 ledger_conflict` / `stale_revision` / `ledger_superseded`
   * / `read_appeared_concurrently`. Renders `CS-CORRECT-CONFLICT`; cleared only by a fresh token arriving from a
   * re-fetch, which is the user-visible recovery §5 specifies.
   */
  conflict: boolean;
  /**
   * A refusal that is about THIS edit rather than about the review's state — too long, bad characters, a value
   * the §2 schema rejects. Distinct from `conflict` because the remedy is different: change the value, not
   * re-fetch the review.
   */
  rejected: null | { field: string | null; reason: 'too_long' | 'invalid_characters' | 'invalid_value' };
  /** An answer this contract does not recognize, or a transport failure. Renders the unavailability string. */
  failed: boolean;
}

export function initialCorrectionState(cvId: string): CorrectionState {
  return { cvId, precondition: null, busy: false, conflict: false, rejected: null, failed: false };
}

/**
 * EVERY ACTION NAMES ITS CV — not just `issued` (R5.3 round 2, finding 3).
 *
 * Round 1 guarded only `issued`, on the reasoning that the token is the thing that would actually be PRESENTED
 * later. The build review found the hole in that: a PATCH or a conflict refetch started against CV A can settle
 * AFTER the applicant has switched to CV B, and `settled` carries a token — so CV A's token was restored onto CV
 * B's state, from which it would then be presented against a ledger it does not name. That is the exact write-
 * against-somebody-else's-state failure rule 2 exists to foreclose, arriving through the back door.
 *
 * `conflicted`, `rejected` and `failed` carry no token, and are guarded for a quieter but equally real reason:
 * they raise NOTICES. A late conflict from CV A would tell the applicant that the review they are now looking at
 * "changed since you opened it", which is false, and would disable editing on a CV nothing went wrong with.
 *
 * The reducer already knows the current CV — `s.cvId`, reset synchronously on every source change — so the guard
 * is one comparison in one place, applied uniformly rather than per-action-by-judgment.
 */
export type CorrectionAction =
  /** A new CV (or a forced reload). Everything about the previous source is dropped, synchronously. */
  | { type: 'reset'; cvId: string }
  /** The server issued a precondition, on a read/extract/patch response for `cvId`. */
  | { type: 'issued'; cvId: string; precondition: Precondition | null }
  /** A submit was accepted by the gate below and is now in flight. */
  | { type: 'submitting'; cvId: string }
  /** The PATCH settled with a fresh token. */
  | { type: 'settled'; cvId: string; precondition: Precondition | null }
  /** The PATCH was refused on the review's state. */
  | { type: 'conflicted'; cvId: string }
  /** The PATCH was refused on this edit's own value. */
  | { type: 'rejected'; cvId: string; field: string | null; reason: 'too_long' | 'invalid_characters' | 'invalid_value' }
  /** Transport, or an answer outside the contract. */
  | { type: 'failed'; cvId: string };

export function correctionReducer(s: CorrectionState, a: CorrectionAction): CorrectionState {
  // THE SOURCE-GENERATION GUARD, ONCE, FOR EVERY ACTION BUT `reset` (finding 3). `reset` is the one action that
  // legitimately names a DIFFERENT cv than the state holds — it is what changes it — so it is checked first and
  // everything after it is inert unless it names the CV this state is currently for.
  if (a.type !== 'reset' && a.cvId !== s.cvId) return s;
  switch (a.type) {
    case 'reset':
      // A FULL RESET, not a merge. Rule 2: nothing about the previous CV survives into this one — not the token,
      // not the conflict notice, not a rejection about a field that may not even exist on the new ledger.
      return initialCorrectionState(a.cvId);
    case 'issued':
      // (The late-response guard is above, and now covers every action rather than this one alone.)
      // A FRESH TOKEN RE-ENABLES EDITING BUT DOES NOT CLEAR THE CONFLICT NOTICE. §5's recovery is "re-fetch, then
      // the user retries" — two steps, and only the first is ours. Clearing `CS-CORRECT-CONFLICT` the instant the
      // re-fetch lands would flash "this review changed since you opened it" and take it away again before the
      // applicant has read the review it changed into. The notice stays until their own retry settles.
      return { ...s, precondition: a.precondition, failed: false };
    case 'submitting':
      return { ...s, busy: true, rejected: null, failed: false };
    case 'settled':
      return { ...s, busy: false, precondition: a.precondition, conflict: false, rejected: null, failed: false };
    case 'conflicted':
      // THE TOKEN IS CLEARED, not replaced. Editing is disabled until a re-fetch issues a new one, so there is no
      // state in which the applicant can push the same edit again against state nobody re-read.
      return { ...s, busy: false, precondition: null, conflict: true };
    case 'rejected':
      // The token SURVIVES: nothing was written, the review did not move, and the applicant's remedy is to change
      // the value and submit again.
      return { ...s, busy: false, rejected: { field: a.field, reason: a.reason } };
    case 'failed':
      return { ...s, busy: false, failed: true };
  }
}

/** Rule 3 + rule 4, as one predicate the panel asks before rendering any control as enabled. */
export function canSubmit(s: CorrectionState): boolean {
  return s.precondition !== null && !s.busy;
}

/* ============================================================================================================== *
 * THE CORRECTION ITSELF
 * ============================================================================================================== */

/** The three §5 correction classes, as the panel issues them. */
export type Correction =
  /**
   * §2.8 `user_edited` — one field, one new value.
   *
   * `value` IS `unknown`, NOT `string` (R5.3 round 2, finding 4). Editing now covers every §2 fact class, and
   * three of them are not strings: `team_size` is an integer, `is_ongoing` is a boolean, and every field admits
   * the literal `'not_stated'`. Sending `"3"` where §2 says `3` would be refused by the server's own validator —
   * correctly — so the panel coerces by KIND (`coerceEditValue`) and `prepareEdit` validates the typed result.
   */
  | { op: 'edit'; entryId: string; field: string; value: unknown }
  /** §2.8 `user_flagged_wrong` on the WHOLE entry — excluded from band computation until it is corrected. */
  | { op: 'flag_entry'; entryId: string }
  /**
   * §5 step 6's "re-editing lifts the exclusion", as the ratified `CS-FLAG-WRONG-UNDO` control.
   *
   * It resolves to `user_confirmed`, NOT to `extracted`: §2.8 is a one-way door out of `extracted` (the server
   * refuses the return with `400 invalid_state_transition`), and "Undo — this was correct" is, in the applicant's
   * own words, a confirmation. So the undo is an ordinary `confirm`-class correction, which is also what makes it
   * recompute and lift the exclusion in the same transaction as everything else.
   */
  | { op: 'unflag_entry'; entryId: string }
  /**
   * §5 step 1's THIRD correction class, finally given a control of its own (R5.3 round 2, ruling 1).
   *
   * §5 names three classes — edit, confirm, flag — and round 1 shipped a control for two: `confirm` was reachable
   * only sideways, through `CS-FLAG-WRONG-UNDO`, which reads as an undo and is offered only on an already-flagged
   * entry. The build review ruled that filing the gap was the right call and that the gap was still blocking, so
   * the label is registered (`CS-CONFIRM-ENTRY-ACTION`, Appendix A.5-P) and the control exists.
   *
   * IT IS ENTRY-SCOPED, and the label says so. `confirmation_state` is a column on `fact_ledger_entries` (§2.2)
   * and the PATCH route accepts it for an entry and nothing smaller; a per-FACT "Confirm" that silently marked
   * the whole entry confirmed would be the same false affordance the review rejected for evidence provenance.
   * The wire body is identical to `unflag_entry`'s — both resolve to §2.8 `user_confirmed` — and they stay
   * separate ops because they are separate things to say, and they carry separate labels.
   */
  | { op: 'confirm_entry'; entryId: string }
  /**
   * §2.2a G6 — the ATOMIC signal flag. One fact, not the entry: "a user who tells us we misread 'led' cannot be
   * made to retract the setting we read correctly on the same line." This is the correction class T-FLAG-1b's
   * band-movement contract runs through.
   */
  | { op: 'flag_fact'; entryId: string; factId: string };

/**
 * What the panel learns from one correction. Every member is a state the server actually reports.
 *
 * `affectedDomainIds` rides on every SUCCESSFUL member (round 2, finding 6). §5 requires `CS-FLAG-ESCALATED` in
 * both places — the ledger card and every domain that lost a unit — and only the server can say which domains
 * those are: after the recompute the excluded unit is gone from every list, so the "before" read the correction
 * replaced is the only place its membership still exists. Empty for a correction that excluded nothing, which is
 * a real answer rather than a missing one.
 */
export type CorrectionResult =
  /** A recompute happened: a new read, and the band moves it caused (possibly none). */
  | { kind: 'recomputed'; entry: LedgerEntryEcho | null; read: RubricReadPublicV1; precondition: string | null; affectedDomainIds: string[] }
  /** §5 step 0 — states N/L. The ledger moved; no read exists to recompute, and none was manufactured. */
  | { kind: 'ledger_only'; entry: LedgerEntryEcho | null; precondition: string | null; affectedDomainIds: string[] }
  /** §5 step 0.5 — an exact retry, answered from the stored result. Nothing was appended. */
  | { kind: 'replayed'; entry: LedgerEntryEcho | null; read: RubricReadPublicV1 | null; precondition: string | null; affectedDomainIds: string[] }
  /** The review moved under us. `CS-CORRECT-CONFLICT`; the recovery is a re-fetch. */
  | { kind: 'conflict' }
  /** State X — `409 correction_requires_recovery`. The remedy is the free `/refresh`, not a retry. */
  | { kind: 'requires_recovery' }
  /** This edit's own value was refused. */
  | { kind: 'rejected'; field: string | null; reason: 'too_long' | 'invalid_characters' | 'invalid_value' }
  /** Transport, or an answer this contract does not recognize. */
  | { kind: 'failed' };

/** The ledger echo, as far as the panel reads it. Structurally typed: unknown fields are ignored, never trusted. */
export interface LedgerEntryEcho {
  entry_id?: unknown;
  confirmation_state?: unknown;
  facts?: unknown;
  role_title?: unknown;
  extraction_uncertain?: unknown;
}

interface PatchBody {
  entry?: LedgerEntryEcho | null;
  read?: unknown;
  ledger_only?: boolean;
  idempotent_replay?: boolean;
  precondition?: unknown;
  /** §5 — the domains the exclusion reached, derived server-side over the read this correction replaced. */
  affected_domain_ids?: unknown;
  error?: string;
  field?: string;
}

/**
 * Narrow `affected_domain_ids` STRICTLY, for the same reason `precondition` is narrowed strictly: a backend that
 * predates the field must produce "no domains were named", never a coerced value that would put an escalation
 * notice on a domain nobody reported.
 */
function affectedDomainIdsOf(body: PatchBody): string[] {
  const v = body.affected_domain_ids;
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x.length > 0) : [];
}

/** The typed 409s that mean "the review moved", all of which recover the same way: re-fetch, then re-present. */
const CONFLICT_ERRORS = new Set([
  'ledger_conflict',
  'stale_revision',
  'stale_revision_precondition',
  'ledger_superseded',
  'read_appeared_concurrently',
  // A reused key for a genuinely different edit is a client-side bug, but its remedy is the same from here: mint a
  // fresh key and resubmit against re-fetched state. Reported as a conflict rather than swallowed.
  'correction_key_reused',
]);

/**
 * NORMALIZE AND BOUND AN EDIT BEFORE IT IS SENT (§1.5's client half).
 *
 * Runs the SAME `inertString` the server runs, from the same module, so what the applicant is told about their
 * value and what the server will do with it cannot disagree. Returns the normalized value on success — the panel
 * shows THAT back, so the field holds the string that will actually be stored (NFC-composed, zero-width and bidi
 * controls stripped) rather than the pre-normalization string the applicant pasted.
 */
export function prepareEdit(field: string, raw: unknown):
  | { ok: true; value: unknown }
  | { ok: false; reason: 'too_long' | 'invalid_characters' | 'invalid_value' } {
  if (!EDITABLE_FIELDS.has(field)) return { ok: false, reason: 'invalid_value' };
  // ONLY A STRING GOES THROUGH THE INERT BOUNDARY, because that boundary is about TEXT: NFC composition, control
  // characters, the newline rule, and a length bound in code points. A number or a boolean has none of those
  // properties, and running `String(v)` through the normalizer only to hand the number back would be a coercion
  // pretending to be a check.
  const value: unknown = typeof raw === 'string' ? undefined : raw;
  if (typeof raw === 'string') {
    const inert = inertString(field, raw);
    if (!inert.ok) {
      return { ok: false, reason: inert.reason === 'too_long' ? 'too_long' : 'invalid_characters' };
    }
    // The §2 schema check, through the server's own validator rather than a client-side restatement of it. An
    // empty string is not a value; `not_stated` is how §2.1 spells absence, and the panel offers it explicitly.
    if (!EDITABLE_FIELDS.get(field)!(inert.value)) return { ok: false, reason: 'invalid_value' };
    return { ok: true, value: inert.value };
  }
  if (!EDITABLE_FIELDS.get(field)!(value)) return { ok: false, reason: 'invalid_value' };
  return { ok: true, value };
}

/** §2.1's absence literal, spelled once. Offered as an explicit option wherever the field's schema admits it. */
export const NOT_STATED = 'not_stated';

/**
 * A control's string output → the §2 TYPED value for its field.
 *
 * THE DOM ONLY HAS STRINGS. `<select>` gives `"true"`, `<input type="number">` gives `"3"`, and §2 wants `true`
 * and `3`. This is the one place that conversion happens, keyed off the same `FactValueKind` the renderer and the
 * editor-chooser use, so a field cannot be rendered as one kind and coerced as another.
 *
 * A value it cannot convert is passed through UNCHANGED rather than guessed at — `prepareEdit`'s validator then
 * refuses it, which reports `invalid_value` to the applicant instead of silently sending a `NaN`.
 */
export function coerceEditValue(field: string, raw: string): unknown {
  if (raw === NOT_STATED) return NOT_STATED;
  switch (presentationFor(field)?.kind) {
    case 'boolean':
      return raw === 'true' ? true : raw === 'false' ? false : raw;
    case 'integer':
    case 'number': {
      const n = Number(raw);
      return raw.trim().length > 0 && Number.isFinite(n) ? n : raw;
    }
    default:
      return raw;
  }
}

/** The request body for one correction, by class. */
function bodyFor(c: Correction): Record<string, unknown> {
  switch (c.op) {
    case 'edit':
      return { confirmation_state: 'user_edited', edits: { [c.field]: c.value } };
    case 'flag_entry':
      return { confirmation_state: 'user_flagged_wrong' };
    case 'unflag_entry':
    case 'confirm_entry':
      return { confirmation_state: 'user_confirmed' };
    case 'flag_fact':
      return { flag_facts: [c.factId] };
  }
}

/**
 * Mint a correction key (§5 step 0.5).
 *
 * A FRESH KEY PER USER ACTION, not per network attempt. That is the distinction §5's step-0.5 rules rest on: an
 * exact transport retry re-sends the same key AND the same `If-Match`, and is answered from the stored result or
 * refused as a stale precondition without a second mutation; a re-fetch-and-resubmit is a NEW user action with a
 * new key and a new precondition, and is a legitimately new write. This client never retries automatically, so
 * every key it mints corresponds to exactly one thing the applicant did.
 */
export function mintCorrectionKey(): string {
  const g = globalThis.crypto;
  if (g && typeof g.randomUUID === 'function') return g.randomUUID();
  // No `Math.random` fallback that could collide across tabs: without `crypto`, the key is omitted entirely and
  // the server falls back to hashing the request's own canonical body, which is what it already does for a
  // key-less correction (`correction_key: correctionKey ?? bodyHash`).
  return '';
}

export async function submitCorrection(
  c: Correction,
  precondition: Precondition,
  correctionKey: string,
): Promise<CorrectionResult> {
  let res: Awaited<ReturnType<typeof api<PatchBody>>>;
  try {
    res = await api<PatchBody>(`/api/fact-ledger/entries/${encodeURIComponent(c.entryId)}`, {
      method: 'PATCH',
      // THE PRECONDITION IS PRESENTED VERBATIM — the server-issued string, unparsed and un-incremented (rule 1).
      headers: correctionKey
        ? { 'If-Match': precondition, 'X-Correction-Key': correctionKey }
        : { 'If-Match': precondition },
      body: JSON.stringify(bodyFor(c)),
    });
  } catch {
    return { kind: 'failed' };
  }

  const body = res.data ?? {};
  const nextPrecondition = typeof body.precondition === 'string' ? body.precondition : null;
  const affectedDomainIds = affectedDomainIdsOf(body);

  if (res.ok) {
    if (body.ledger_only === true) {
      return { kind: 'ledger_only', entry: body.entry ?? null, precondition: nextPrecondition, affectedDomainIds };
    }
    // THE READ IS VALIDATED BEFORE ANYTHING READS IT — the same structural validator the GET runs, for the same
    // reason (round-2 finding 3): a truthy `read` is not a contract, and a partially-deployed backend answering
    // `200 {read: {…old shape…}}` must reach the unavailability state rather than a half-rendered review.
    const read = isValidPublicShape(body.read) ? body.read : null;
    if (body.idempotent_replay === true) {
      return { kind: 'replayed', entry: body.entry ?? null, read, precondition: nextPrecondition, affectedDomainIds };
    }
    if (read === null) return { kind: 'failed' };
    return { kind: 'recomputed', entry: body.entry ?? null, read, precondition: nextPrecondition, affectedDomainIds };
  }

  const error = typeof body.error === 'string' ? body.error : '';
  if (res.status === 409 && error === 'correction_requires_recovery') return { kind: 'requires_recovery' };
  if (res.status === 409 && CONFLICT_ERRORS.has(error)) return { kind: 'conflict' };
  // 428/400 on the precondition itself: we presented none, or one this grammar does not accept. Both mean the
  // client's own token is unusable, and both recover the same way a conflict does — re-fetch, re-present.
  if (res.status === 428 || error === 'bad_precondition') return { kind: 'conflict' };
  if (res.status === 413 && error === 'value_too_long') {
    return { kind: 'rejected', field: typeof body.field === 'string' ? body.field : null, reason: 'too_long' };
  }
  if (res.status === 400 && error === 'invalid_characters') {
    return { kind: 'rejected', field: typeof body.field === 'string' ? body.field : null, reason: 'invalid_characters' };
  }
  if (res.status === 400) {
    return { kind: 'rejected', field: typeof body.field === 'string' ? body.field : null, reason: 'invalid_value' };
  }
  return { kind: 'failed' };
}
